/**
 * Standalone Hotels Portal - Main Dashboard
 * 
 * Clean, isolated Hotels vertical with NO dependencies on:
 * - Restaurant portal
 * - Automotive portal
 * - Healthcare portal
 * - Industry selectors
 * - SSO bridges
 * 
 * Full bilingual support (EN/FR).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  MapPin,
  CreditCard,
  Users,
  TrendingUp,
  Plus,
  Settings,
  LogOut,
  Activity,
  BarChart3,
  Copy,
  ExternalLink,
  ClipboardCheck,
  Star,
  Globe,
  ChevronLeft,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/hotels-standalone`;
// PUBLIC_URL is the production domain for NFC card programming (what guests access)
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || API_BASE;

export default function StandaloneHotelsDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Accept either standalone token or main portal token
  const token = localStorage.getItem('hotels_token') || localStorage.getItem('token');

  // Force dark mode for standalone pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
    return () => {
      // Cleanup: restore previous theme preference on unmount
      const savedTheme = localStorage.getItem('yul_theme_preference');
      if (savedTheme !== 'dark') {
        document.documentElement.classList.remove('dark');
      }
    };
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/standalone/login');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      // Fetch dashboard stats
      const statsRes = await fetch(`${API_URL}/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!statsRes.ok) throw new Error('Failed to fetch stats');
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch organizations
      const orgsRes = await fetch(`${API_URL}/organizations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!orgsRes.ok) throw new Error('Failed to fetch organizations');
      const orgsData = await orgsRes.json();
      setOrganizations(orgsData);

      // Fetch current user
      const userRes = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('hotels_token');
    localStorage.removeItem('hotels_user');
    navigate('/standalone/login');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('yul_language', newLang);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(222,47%,11%)] flex items-center justify-center">
        <div className="text-white">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(222,47%,11%)]">
      {/* Header */}
      <header className="bg-[hsl(217,19%,12%)] border-b border-[hsl(217,19%,27%)] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">HOTELS ON TAP</h1>
              <p className="text-sm text-[hsl(215,20%,65%)]">{t('standalone.login.title')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/hotels')}
              className="text-[hsl(215,20%,65%)] hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {i18n.language === 'fr' ? 'Retour aux Propriétés' : 'Back to Properties'}
            </Button>
            <span className="text-sm text-[hsl(215,20%,65%)]">
              {user?.email} ({user?.role})
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-[hsl(215,20%,65%)] hover:text-white"
            >
              <Globe className="w-4 h-4 mr-2" />
              {i18n.language === 'en' ? 'FR' : 'EN'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[hsl(215,20%,65%)] hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[hsl(215,20%,65%)]">{t('standalone.dashboard.stats.totalOrganizations')}</p>
                  <p className="text-2xl font-bold text-white">{stats?.total_organizations || 0}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[hsl(215,20%,65%)]">{t('hotels.locations')}</p>
                  <p className="text-2xl font-bold text-white">{stats?.total_locations || 0}</p>
                </div>
                <MapPin className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[hsl(215,20%,65%)]">{t('standalone.dashboard.stats.totalCards')}</p>
                  <p className="text-2xl font-bold text-white">
                    {stats?.active_cards || 0} / {stats?.total_cards || 0}
                  </p>
                </div>
                <CreditCard className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[hsl(215,20%,65%)]">{t('standalone.dashboard.stats.totalTaps')}</p>
                  <p className="text-2xl font-bold text-white">{stats?.total_taps || 0}</p>
                </div>
                <Activity className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                {t('standalone.dashboard.organizations')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {organizations.length === 0 ? (
                <p className="text-[hsl(215,20%,65%)]">{t('common.noData')}</p>
              ) : (
                <div className="space-y-3">
                  {organizations.map((org) => (
                    <div
                      key={org.id}
                      onClick={() => navigate(`/standalone/organization/${org.id}`)}
                      className="flex items-center justify-between p-4 rounded-lg bg-[hsl(222,47%,11%)] hover:bg-[hsl(217,19%,27%)] cursor-pointer transition-colors"
                    >
                      <div>
                        <p className="text-white font-medium">{org.name}</p>
                        <p className="text-sm text-[hsl(215,20%,65%)]">{org.contact_email}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-[hsl(215,20%,65%)]" />
                    </div>
                  ))}
                </div>
              )}
              {user?.role === 'super_admin' && (
                <Button
                  className="mt-4 w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/standalone/organization/new')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('common.add')} {t('standalone.dashboard.organizations')}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                {t('standalone.dashboard.quickActions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Guest Arrival Section */}
              <Button
                className="w-full justify-start bg-green-600/20 hover:bg-green-600/30 border border-green-600/40 text-green-400"
                onClick={() => navigate('/standalone/cards?type=guest_arrival')}
                data-testid="nav-guest-arrival"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                {t('standalone.dashboard.guestArrivalCards')}
              </Button>
              
              {/* Tap Engine Section */}
              <Button
                className="w-full justify-start bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/40 text-blue-400"
                onClick={() => navigate('/standalone/cards?type=tap_engine')}
                data-testid="nav-tap-engine"
              >
                <Star className="w-4 h-4 mr-2" />
                {t('standalone.dashboard.tapEngineCards')}
              </Button>

              <div className="border-t border-[hsl(217,19%,27%)] my-2"></div>

              <Button
                variant="outline"
                className="w-full justify-start border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,27%)]"
                onClick={() => navigate('/standalone/analytics')}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                {t('standalone.dashboard.viewAnalytics')}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,27%)]"
                onClick={() => navigate('/standalone/cards')}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {t('standalone.dashboard.allCards')}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,27%)]"
                onClick={() => navigate('/standalone/employees')}
              >
                <Users className="w-4 h-4 mr-2" />
                {t('standalone.dashboard.manageEmployees')}
              </Button>
              {user?.role === 'super_admin' && (
                <Button
                  variant="outline"
                  className="w-full justify-start border-[hsl(217,19%,27%)] text-white hover:bg-[hsl(217,19%,27%)]"
                  onClick={() => navigate('/standalone/activity-logs')}
                >
                  <Activity className="w-4 h-4 mr-2" />
                  {t('standalone.dashboard.activityLogs')}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* NFC URL Helper */}
        <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              {i18n.language === 'fr' ? 'Format URL Carte NFC' : 'NFC Card URL Format'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-[hsl(215,20%,65%)] mb-1">Review Engine Cards:</p>
                <div className="flex items-center space-x-4 p-3 rounded-lg bg-[hsl(222,47%,11%)]">
                  <code className="flex-1 text-blue-400 font-mono text-sm">
                    {PUBLIC_URL}/standalone/review/&#123;CARD_ID&#125;
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${PUBLIC_URL}/standalone/review/`);
                      toast.success('Review URL prefix copied');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-[hsl(215,20%,65%)] mb-1">Room Survey Tags:</p>
                <div className="flex items-center space-x-4 p-3 rounded-lg bg-[hsl(222,47%,11%)]">
                  <code className="flex-1 text-green-400 font-mono text-sm">
                    {PUBLIC_URL}/standalone/room/&#123;TAG_ID&#125;
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${PUBLIC_URL}/standalone/room/`);
                      toast.success('Room URL prefix copied');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-sm text-[hsl(215,20%,65%)] mt-3">
              Use <span className="text-blue-400">/review/</span> for review cards, <span className="text-green-400">/room/</span> for survey tags
            </p>
          </CardContent>
        </Card>
      </main>
      <footer className="text-center py-3">
        <p className="text-[10px] text-[hsl(215,20%,35%)]" data-testid="dashboard-build-version">Build: 2026-03-01-v3</p>
      </footer>
    </div>
  );
}
